import { DECORATIVE_Z_INDEX } from "@/contexts/ModalContext"
import type { CelebrationKind, SoundEnvelope } from "@/domain/seasonal"

const COLORS = ["primary", "secondary", "success", "accent", "warning", "danger"].map(
  (token) => `rgb(var(--color-${token}))`,
)

function randColor(): string {
  return COLORS[Math.floor(Math.random() * COLORS.length)]
}

/**
 * Confetti container lives on `<body>`, above the modal stack and below toast.
 * Z-index comes from here, not `.celebration-container` in CSS — `globals.css`
 * cannot import TypeScript, so JS keeps the value tied to the constant. See
 * docs/Z-INDEX-LAYERS.md.
 */
function createCelebrationContainer(): HTMLDivElement {
  const container = document.createElement("div")
  container.className = "celebration-container"
  container.style.zIndex = String(DECORATIVE_Z_INDEX)
  document.body.appendChild(container)
  return container
}

function playTada(): void {
  const container = createCelebrationContainer()

  const shapes = ["50%", "3px", "0"]
  const cannons = [15 + Math.random() * 10, 45 + Math.random() * 10, 75 + Math.random() * 10]
  if (Math.random() > 0.4) cannons.push(30 + Math.random() * 10)

  cannons.forEach((cx) => {
    for (let i = 0; i < 35; i++) {
      const piece = document.createElement("div")
      piece.className = "tada-piece"
      const size = 8 + Math.random() * 14
      piece.style.width = size + "px"
      piece.style.height = size + "px"
      piece.style.background = randColor()
      piece.style.borderRadius = shapes[Math.floor(Math.random() * shapes.length)]
      piece.style.left = cx + "%"
      piece.style.bottom = "0"
      const dx = (Math.random() - 0.5) * 500
      const dy = -(200 + Math.random() * 450)
      piece.style.setProperty("--dx", dx + "px")
      piece.style.setProperty("--dy", dy + "px")
      piece.style.setProperty("--rot", 300 + Math.random() * 400 + "deg")
      piece.style.setProperty("--dur", 0.9 + Math.random() * 0.6 + "s")
      piece.style.animationDelay = Math.random() * 0.1 + "s"
      container.appendChild(piece)
    }
  })

  setTimeout(() => container.remove(), 2500)
}

function playConfetti(): void {
  const container = createCelebrationContainer()

  for (let i = 0; i < 50; i++) {
    const piece = document.createElement("div")
    piece.className = "confetti-piece"
    piece.style.left = Math.random() * 100 + "%"
    piece.style.top = "-15px"
    piece.style.background = randColor()
    piece.style.animationDelay = Math.random() * 0.6 + "s"
    piece.style.animationDuration = 1 + Math.random() * 1 + "s"
    piece.style.borderRadius = Math.random() > 0.5 ? "50%" : "3px"
    piece.style.width = 7 + Math.random() * 10 + "px"
    piece.style.height = 7 + Math.random() * 10 + "px"
    container.appendChild(piece)
  }

  setTimeout(() => container.remove(), 3000)
}

const SVG_NS = "http://www.w3.org/2000/svg"
const BAT_WING = "M54 30C42 16 24 9 2 5C9 13 11 21 8 31C15 26 21 28 23 35C28 30 34 32 36 39C41 34 47 36 51 42Z"
const BAT_WING_SHADE = "M50 31C40 21 27 15 12 12C16 18 17 23 16 28C21 26 25 29 27 33C31 30 36 32 38 36C42 33 46 35 48 38Z"
const BAT_EARS = "M51 22L48 7L57 16ZM69 22L72 7L63 16Z"
const BAT_FANGS = "M56.5 32.5L58 36.5L59.5 32.5ZM60.5 32.5L62 36.5L63.5 32.5Z"
const BAT_COUNT_MIN = 60
const BAT_COUNT_SPREAD = 21
const BAT_STRAGGLER_LEVEL = 0.05
const BAT_ENVELOPE_CONTRAST = 1.5
const BAT_CLEANUP_MARGIN_MS = 400
const DEFAULT_BAT_ENVELOPE: SoundEnvelope = { bucketMs: 200, levels: [1, 0.9, 0.7, 0.45, 0.3, 0.18, 0.1, 0.06] }
const BAT_EASINGS = [
  "linear",
  "cubic-bezier(0.3, 0.55, 0.6, 1)",
  "cubic-bezier(0.5, 0.1, 0.55, 0.95)",
  "cubic-bezier(0.2, 0.7, 0.4, 1)",
]

type Point = { x: number; y: number }
type BatLaunch = { atMs: number; straggler: boolean }

function svgEl<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string>): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag)
  for (const [name, value] of Object.entries(attrs)) el.setAttribute(name, value)
  return el
}

function createWing(): SVGGElement {
  const wing = svgEl("g", { class: "bat-wing" })
  wing.append(svgEl("path", { d: BAT_WING, class: "bat-skin" }), svgEl("path", { d: BAT_WING_SHADE, class: "bat-shade" }))
  return wing
}

function createBatSvg(): SVGSVGElement {
  const svg = svgEl("svg", { viewBox: "0 0 120 64", class: "bat-svg", "aria-hidden": "true", focusable: "false" })
  const mirrored = svgEl("g", { transform: "translate(120 0) scale(-1 1)" })
  mirrored.appendChild(createWing())
  svg.append(
    createWing(),
    mirrored,
    svgEl("path", { d: BAT_EARS, class: "bat-skin" }),
    svgEl("ellipse", { cx: "60", cy: "41", rx: "11", ry: "12", class: "bat-skin" }),
    svgEl("circle", { cx: "60", cy: "26", r: "12", class: "bat-skin" }),
    svgEl("circle", { cx: "55.5", cy: "25", r: "3.6", class: "bat-eye" }),
    svgEl("circle", { cx: "64.5", cy: "25", r: "3.6", class: "bat-eye" }),
    svgEl("circle", { cx: "55.8", cy: "25.4", r: "1.6", class: "bat-pupil" }),
    svgEl("circle", { cx: "64.2", cy: "25.4", r: "1.6", class: "bat-pupil" }),
    svgEl("path", { d: BAT_FANGS, class: "bat-eye" }),
  )
  return svg
}

function between(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function batLaunches(envelope: SoundEnvelope, total: number): BatLaunch[] {
  const { bucketMs, levels } = envelope
  const weights = levels.map((level) => level ** BAT_ENVELOPE_CONTRAST)
  const sum = weights.reduce((acc, weight) => acc + weight, 0) || 1
  const launches: BatLaunch[] = []
  let cumulative = 0
  weights.forEach((weight, bucket) => {
    cumulative += weight
    const target = Math.round((cumulative / sum) * (total - 1))
    while (launches.length < target) launches.push({ atMs: (bucket + Math.random()) * bucketMs, straggler: false })
  })
  let last = levels.length - 1
  while (last > 0 && levels[last] < BAT_STRAGGLER_LEVEL) last--
  launches.push({ atMs: (last + 0.5) * bucketMs, straggler: true })
  return launches
}

function smoothPath(points: Point[]): string {
  const at = (i: number) => points[Math.max(0, Math.min(points.length - 1, i))]
  const r = (n: number) => n.toFixed(1)
  let d = `M${r(points[0].x)} ${r(points[0].y)}`
  for (let i = 0; i < points.length - 1; i++) {
    const [p0, p1, p2, p3] = [at(i - 1), at(i), at(i + 1), at(i + 2)]
    const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 }
    const c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 }
    d += ` C${r(c1.x)} ${r(c1.y)} ${r(c2.x)} ${r(c2.y)} ${r(p2.x)} ${r(p2.y)}`
  }
  return d
}

function flightPath(width: number, height: number, startX: number, sizePx: number, wildness: number): string {
  const startY = height + sizePx
  const endY = -sizePx * 2 - between(0, 0.15) * height
  const legs = 3 + Math.floor(Math.random() * 3)
  const points: Point[] = [{ x: startX, y: startY }]
  for (let leg = 1; leg <= legs; leg++) {
    const previous = points[leg - 1]
    const progress = (leg + (leg < legs ? between(-0.25, 0.25) : 0)) / legs
    const swing = between(-1, 1) * width * 0.24 * wildness
    points.push({
      x: Math.max(-0.1 * width, Math.min(1.05 * width, previous.x + swing)),
      y: startY + (endY - startY) * progress,
    })
  }
  return smoothPath(points)
}

function createBat(launch: BatLaunch, width: number, height: number): HTMLDivElement {
  const depth = launch.straggler ? between(0.55, 0.75) : Math.random() ** 1.3
  const sizePx = 28 + depth * 64
  const startX = between(-0.05, 0.95) * width
  const drift = between(-22, 22)
  const dur = (launch.straggler ? between(2.4, 2.8) : (3.2 - depth * 1.7) * between(0.85, 1.15)).toFixed(2)
  const flyer = document.createElement("div")
  flyer.className = "bat-flyer"
  flyer.style.setProperty("--path", `path("${flightPath(width, height, startX, sizePx, launch.straggler ? 1.3 : between(0.5, 1))}")`)
  flyer.style.setProperty("--from-x", (startX / width) * 100 + "vw")
  flyer.style.setProperty("--to-x", (startX / width) * 100 + drift + "vw")
  flyer.style.setProperty("--from-y", between(102, 115) + "vh")
  flyer.style.setProperty("--to-y", -between(25, 40) + "vh")
  flyer.style.setProperty("--dur", dur + "s")
  flyer.style.setProperty("--ease", BAT_EASINGS[Math.floor(Math.random() * BAT_EASINGS.length)])
  flyer.style.setProperty("--size", sizePx.toFixed(0) + "px")
  flyer.style.opacity = (0.5 + depth * 0.5).toFixed(2)
  flyer.style.zIndex = String(Math.round(depth * 10))
  flyer.style.animationDelay = (launch.atMs / 1000).toFixed(2) + "s"
  if (launch.straggler) flyer.dataset.straggler = "true"

  const bob = document.createElement("div")
  bob.className = "bat-bob"
  bob.style.setProperty("--amp", between(-3, 3) + "vw")
  bob.style.setProperty("--bob", between(0.25, 0.6) + "s")

  const svg = createBatSvg()
  svg.style.setProperty("--flap", between(0.07, 0.13) + "s")
  if (Math.random() > 0.5) svg.classList.add("bat-svg-flip")
  bob.appendChild(svg)
  flyer.appendChild(bob)
  return flyer
}

function playBats(envelope: SoundEnvelope = DEFAULT_BAT_ENVELOPE): void {
  if (prefersReducedMotion()) return
  const container = createCelebrationContainer()
  container.setAttribute("aria-hidden", "true")
  const width = window.innerWidth
  const height = window.innerHeight
  const total = BAT_COUNT_MIN + Math.floor(Math.random() * BAT_COUNT_SPREAD)
  let endMs = 0

  for (const launch of batLaunches(envelope, total)) {
    const flyer = createBat(launch, width, height)
    endMs = Math.max(endMs, launch.atMs + parseFloat(flyer.style.getPropertyValue("--dur")) * 1000)
    container.appendChild(flyer)
  }

  setTimeout(() => container.remove(), endMs + BAT_CLEANUP_MARGIN_MS)
}

/** Options of `celebrate`; `envelope` paces the bat flock to its sound (many bats on loud parts, a last straggler). */
export type CelebrateOptions = { envelope?: SoundEnvelope }

/** Plays the order celebration: confetti by default, a bat flock for Halloween. */
export function celebrate(kind: CelebrationKind = "confetti", { envelope }: CelebrateOptions = {}): void {
  if (kind === "bats") {
    playBats(envelope)
    return
  }
  playTada()
  playConfetti()
}
